import React from 'react'
import styled from 'styled-components'

import { Result, Option } from '@blade/std-lib'

import { XEmbedResizePostMessage } from '../model/x-embed-resize-post-message'

import { loadXWidget } from './load-x-widget'

import type { Appearance } from '@blade/ui-theme'
import type { Lang } from '@root/shared/localization/model/Lang'

export interface TwitterTweetEmbedProps {
  tweetId: string
  lang: Lang
  theme: Appearance
  onLoad?: (element: HTMLElement) => void
  onError?: (error: unknown) => void
  onResize?: (size: { height: number; width: number }) => void
}

export function XTweetEmbed({
  tweetId,
  lang,
  theme,
  onLoad,
  onError,
  onResize,
}: TwitterTweetEmbedProps) {
  const ref = React.useRef<HTMLDivElement>(null)
  const onLoadRef = React.useRef(onLoad)
  const onErrorRef = React.useRef(onError)
  const onResizeRef = React.useRef(onResize)
  React.useLayoutEffect(() => {
    onLoadRef.current = onLoad
    onErrorRef.current = onError
    onResizeRef.current = onResize
  })

  const [_loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    let cancelled = false

    const container = ref.current

    if (!container) {
      return
    }

    async function run(currentContainer: HTMLDivElement) {
      setLoading(true)

      currentContainer.replaceChildren()

      const result = await loadXWidget()

      if (Result.isFailure(result)) {
        setLoading(false)
        onErrorRef.current?.(result.failure)

        return
      }

      const widgets = result.success

      try {
        const element = await widgets.createTweet(tweetId, currentContainer, {
          lang,
          theme,
        })

        if (cancelled) {
          return
        }

        setLoading(false)

        onLoadRef.current?.(element)
      } catch (error) {
        if (cancelled) {
          return
        }
        setLoading(false)

        onErrorRef.current?.(error)
      }
    }

    run(container)

    return () => {
      cancelled = true

      container.replaceChildren()
    }
  }, [tweetId, lang, theme])

  React.useEffect(() => {
    const container = ref.current

    if (!container) {
      return
    }

    const handleMessage = (event: MessageEvent) => {
      const iframe = container.querySelector('iframe')

      if (!iframe || event.source !== iframe.contentWindow) {
        return
      }

      const result = XEmbedResizePostMessage.decode(event.data)

      if (Option.isNone(result)) {
        return
      }

      const resizeMessageData = result.value['twttr.embed'].params[0]

      if (resizeMessageData.data.tweet_id !== tweetId) {
        return
      }

      onResizeRef.current?.({
        height: resizeMessageData.height,
        width: resizeMessageData.width,
      })
    }

    window.addEventListener('message', handleMessage)

    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [tweetId])

  return <Root ref={ref} />
}

const Root = styled.div``
